import { motion } from 'motion/react'
import { useInView } from 'motion/react'
import { useRef } from 'react'

function ProblemCard({
  icon,
  title,
  description,
  delay,
}: {
  icon: React.ReactNode
  title: string
  description: string
  delay: number
}) {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-50px' })

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      style={{
        padding: '2rem',
        borderRadius: '16px',
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.07)',
        transition: 'all 0.3s ease',
        cursor: 'default',
        position: 'relative',
        overflow: 'hidden',
      }}
      onMouseEnter={(e) => {
        const el = e.currentTarget as HTMLElement
        el.style.background = 'rgba(157, 78, 221, 0.06)'
        el.style.borderColor = 'rgba(157, 78, 221, 0.25)'
        el.style.transform = 'translateY(-4px)'
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget as HTMLElement
        el.style.background = 'rgba(255,255,255,0.03)'
        el.style.borderColor = 'rgba(255,255,255,0.07)'
        el.style.transform = 'translateY(0)'
      }}
    >
      {/* Top accent line */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: '2rem',
          right: '2rem',
          height: '1px',
          background:
            'linear-gradient(90deg, transparent, rgba(157, 78, 221, 0.5), transparent)',
        }}
      />

      <div
        style={{
          width: '52px',
          height: '52px',
          borderRadius: '12px',
          background: 'rgba(157, 78, 221, 0.12)',
          border: '1px solid rgba(157, 78, 221, 0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '1.5rem',
          color: '#9D4EDD',
        }}
      >
        {icon}
      </div>

      <h3
        style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: '1.15rem',
          fontWeight: 700,
          color: '#ffffff',
          marginBottom: '0.75rem',
          letterSpacing: '-0.01em',
        }}
      >
        {title}
      </h3>
      <p
        style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: '0.9rem',
          color: 'rgba(255,255,255,0.5)',
          lineHeight: 1.65,
          margin: 0,
        }}
      >
        {description}
      </p>
    </motion.div>
  )
}

export function Problem() {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-100px' })

  return (
    <section
      id="problem"
      style={{
        padding: '7rem 2rem',
        background: '#0A0F1F',
        position: 'relative',
      }}
    >
      {/* Subtle divider gradient */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background:
            'linear-gradient(90deg, transparent, rgba(157, 78, 221, 0.3), transparent)',
        }}
      />

      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            <span
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '0.75rem',
                fontWeight: 600,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: '#9D4EDD',
                display: 'block',
                marginBottom: '1rem',
              }}
            >
              The Legacy Trap
            </span>
            <h2
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: 800,
                color: '#ffffff',
                letterSpacing: '-0.03em',
                lineHeight: 1.1,
                marginBottom: '1.25rem',
              }}
            >
              Legacy ETL is holding you back.
            </h2>
            <p
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '1.05rem',
                color: 'rgba(255,255,255,0.5)',
                maxWidth: '580px',
                margin: '0 auto',
                lineHeight: 1.7,
              }}
            >
              For 20+ years, data engineering has meant brittle pipelines,
              endless maintenance, and manual schema mapping. It's time for an
              evolution. You need{' '}
              <span style={{ color: 'rgba(255,255,255,0.85)' }}>
                intelligence, not just integration.
              </span>
            </p>
          </motion.div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '1.5rem',
          }}
        >
          <ProblemCard
            delay={0.1}
            title="Brittle Pipelines"
            description="Prone to failure at every junction. One API change, one schema update, one unexpected null — and your entire data flow breaks. High maintenance, low confidence."
            icon={
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M6 8h4l2-4 2 4h4" />
                <path d="M6 16h4l2 4 2-4h4" />
                <line x1="12" y1="8" x2="12" y2="11" />
                <line x1="10" y1="12" x2="14" y2="12" strokeDasharray="2 2" />
                <line x1="12" y1="13" x2="12" y2="16" />
              </svg>
            }
          />
          <ProblemCard
            delay={0.2}
            title="Manual Schema Drift"
            description="Your data schemas evolve constantly. Constant human intervention is required to keep mappings alive. Engineers become glorified babysitters of brittle YAML files."
            icon={
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="7" cy="7" r="3" />
                <circle cx="17" cy="17" r="3" />
                <path d="M10 7h4" />
                <path d="M7 10v4" strokeDasharray="2 2" />
                <path d="M10 17h4" />
                <path d="M17 10v4" strokeDasharray="2 2" />
                <path d="M9.5 9.5l5 5" />
              </svg>
            }
          />
          <ProblemCard
            delay={0.3}
            title="Static Insights"
            description="Reports are outdated before they're built. Dashboards show yesterday's reality. In a world that moves in milliseconds, static analytics is the same as no analytics."
            icon={
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="4 18 8 12 12 15 16 9 20 6" />
                <circle cx="19" cy="5" r="2" strokeDasharray="2 2" />
                <text
                  x="18"
                  y="6"
                  fontSize="8"
                  fill="currentColor"
                  stroke="none"
                  fontWeight="bold"
                >
                  ?
                </text>
                <line x1="4" y1="20" x2="20" y2="20" />
              </svg>
            }
          />
        </div>
      </div>
    </section>
  )
}
